import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, setUnauthorizedHandler } from "@/lib/api";

const AuthCtx = createContext(null);

export const ROLE_INFO = {
  admin: { label: "Administrador", desc: "Acesso total: usuários, produtos, conferências e relatórios." },
  gerente: { label: "Gerente", desc: "Produtos, categorias, conferências (finalizar) e relatórios." },
  operador: { label: "Operador", desc: "Apenas registra contagens em conferências abertas." },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const clearSession = useCallback(() => {
    setUser(null);
    qc.clear();
  }, [qc]);

  const logout = useCallback(async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => {});
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    api("/auth/me").then(setUser).catch(() => setUser(null)).finally(() => setLoading(false));
  }, [clearSession]);

  const login = useCallback(async (email, password) => {
    const r = await api("/auth/login", { method: "POST", body: { email, password } });
    setUser(r.user);
  }, []);

  const register = useCallback(async (name, email, password) => {
    const r = await api("/auth/register", { method: "POST", body: { name, email, password } });
    if (r.pending) return { pending: true };
    setUser(r.user);
    return {};
  }, []);

  const isSuper = user?.role === "superadmin";
  const isAdmin = user?.role === "admin";
  const isMgmt = isAdmin || user?.role === "gerente";
  return (
    <AuthCtx.Provider value={{ user, loading, isAdmin, isMgmt, isSuper, login, register, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
